import {DateFormat, TimeFormat, formatDateFns} from '@shared/helpers/date'
import {clone} from '@shared/helpers/lang'
import {has, createStateMaker} from '@shared/helpers/object'

import {PaymentGatewayCode, SubscribePayMethod} from '@web/core/constants'

import {MobilePaymentOpen, MobilePaymentStatus} from '../constants'
import {BillingPayMethods, MobiliansPayMethod, PriorityPaymentMethodState} from '../interfaces'

export const splitDateTimeToDateAndTime = (dateTime: string) => {
    return {
        date: formatDateFns(dateTime, DateFormat['yyyy.MM.dd']) ?? '',
        time: formatDateFns(dateTime, TimeFormat['HH:mm']) ?? '',
    }
}

export const isMobiliansPayMethod = (payMethod: unknown): payMethod is MobiliansPayMethod => {
    return ['sId', 'svcId'].every((path) => has(path, payMethod))
}

/** 간편카드 정렬 로직, card slice 참조 */
export const sortCardEasyPaysItems =
    <T extends object>(sortCallback: (item: T) => boolean) =>
    (cardEasyPays: T[]): T[] => {
        // 첫번째 카드가 점검중이거나 가맹점에서 지원하지 않으면 순서 변경
        const index = cardEasyPays.findIndex((item) => sortCallback(item))
        return index < 0
            ? cardEasyPays
            : [
                  ...(cardEasyPays[index] ? [cardEasyPays[index]] : []),
                  ...cardEasyPays.slice(0, index),
                  ...cardEasyPays.slice(index + 1),
              ]
    }

/** billingPayMethods 초기값 */
const initialBillingPayMethods: BillingPayMethods = {
    bankEasyPay: {
        pgId: '',
        pgCd: '',
    },
    // TODO cardEasyPay PG 정보 > 하드코딩 제거해야 한다.
    cardEasyPay: {
        pgId: '61043',
        pgCd: '61',
    },
    mobile: {
        pgId: '',
        pgCd: '',
        pgName: '',
    },
    recharge: {
        /** 충전결제 pgId */
        pgId: '80006',
        // 충전결제 pgCd 는 고정값
        pgCd: PaymentGatewayCode.PAY_POINT,
    },
}

export const getInitialState = (): PriorityPaymentMethodState => {
    return {
        payMethod: {
            /** 사용 가능한 결제 수단 타입 */
            availablePayMethodTypes: [],
            /** 간편결졔 카드 목록 */
            cardEasyPays: [],
            /** 충전계좌 목록 */
            chargedAccounts: [],
            /** 간편결제 계좌 목록 */
            bankEasyPays: [],
            /** 결제 수단 정보 */
            billingPayMethods: clone(initialBillingPayMethods),
        },
        /** 선택된 결제 수단 */
        selectedPayment: {
            /** 선택된 PG 정보 키 */
            billingPayMethod: null,
            /** 선택된 결제 수단 */
            selectedMethod: SubscribePayMethod.NONE,
            /** 이전에 선택된 결제 수단 */
            prevSelectedMethod: SubscribePayMethod.NONE,
            /** 충전 결제 */
            chargeAccount: {
                accountKey: '',
            },
            /** 계좌 간편결제 */
            bankEasyPay: {
                accountKey: '',
            },
            /** 카드 간편결제 */
            cardEasyPay: {
                cardId: '',
            },
            /** 일반결제 > 휴대폰 */
            mobile: {
                mobileNumbers: '',
            },
        },
        selectedSecondPayment: {
            /** 선택된 PG 정보 키 */
            billingPayMethod: null,
            /** 선택된 결제 수단 */
            selectedMethod: SubscribePayMethod.NONE,
            /** 충전 결제 */
            chargeAccount: {
                accountKey: '',
            },
            /** 계좌 간편결제 */
            bankEasyPay: {
                accountKey: '',
            },
            /** 카드 간편결제 */
            cardEasyPay: {
                cardId: '',
            },
        },
        isLoadFailedEasyAccounts: false,
        isLoadFailedEasyCards: false,
        isRecentPaymentMethodDeleted: false,
        authKey: null,
        isPreOtbill: '',
        isSubscript: '',
        openType: MobilePaymentOpen.SELF,
        resultUrl: '',
        serverInfo: '',
        mobilePaymentStatus: MobilePaymentStatus.IDLE,
        periodDescription: '',
    }
}

export const makeMock = createStateMaker<PriorityPaymentMethodState>(getInitialState)
